import { useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { Link, useNavigate } from 'react-router-dom';
import { Aviso, Botao, Campo, Cartao } from '../componentes/ui';
import { requisicaoPublica } from '../servicos/http';
import { ErroDaApi } from './ErroDaApi';

/**
 * RN-407, RN-408 — Esqueci minha senha.
 *
 * ## O que faltava
 *
 * As duas rotas são públicas desde o começo. A única tela que as usava era o
 * perfil — autenticada, para quem **já entrou** trocar a senha. Quem perdeu a
 * senha não tinha caminho nenhum: chegava no login, não lembrava, e acabou.
 *
 * ## A resposta é sempre a mesma
 *
 * Pedir o código responde igual para e-mail que existe e para e-mail que não
 * existe. É deliberado: distinguir os dois transformaria esta tela num
 * verificador de cadastro — dá para descobrir quem é cliente da MS3 testando
 * endereços. A tela diz isso em voz alta, senão parece que o e-mail não chegou.
 *
 * ## Uma tela, dois passos
 *
 * Pedir e confirmar ficam na mesma página, e o segundo passo aparece depois do
 * primeiro. O código chega em segundos; mandar a pessoa para outra tela (ou
 * pior, esperar um link no e-mail) só cria um lugar a mais de onde desistir.
 */
export function RecuperarSenha(): JSX.Element {
  const navegar = useNavigate();

  const [email, setEmail] = useState('');
  const [codigo, setCodigo] = useState('');
  const [senha, setSenha] = useState('');
  const [confirmacao, setConfirmacao] = useState('');

  const pedir = useMutation({
    mutationFn: () =>
      requisicaoPublica<{ success: true }>('/auth/recuperar-senha', {
        metodo: 'POST',
        corpo: { emailAddress: email.trim().toLowerCase() },
      }),
  });

  const confirmar = useMutation({
    mutationFn: () =>
      requisicaoPublica<unknown>('/auth/recuperar-senha/confirmar', {
        metodo: 'POST',
        corpo: {
          emailAddress: email.trim().toLowerCase(),
          codigo: codigo.trim().toUpperCase(),
          novaSenha: senha,
        },
      }),
    onSuccess: () => {
      // Sem sessão automática: trocar a senha e entrar são duas coisas, e
      // entrar sozinho depois de uma troca esconderia de quem não pediu a troca
      // que ela aconteceu.
      navegar('/entrar', { replace: true });
    },
  });

  const senhasBatem = senha !== '' && senha === confirmacao;

  return (
    <main className="mx-auto flex min-h-dvh w-full max-w-md flex-col justify-center gap-6 px-4 py-12">
      <header className="text-center">
        <p className="font-display text-2xl font-semibold tracking-tight">Deca</p>
        <p className="mt-1 text-sm text-conteudo-secundario">MS3 Capital · Créditos de ICMS</p>
      </header>

      <Cartao
        como="section"
        titulo="Recuperar senha"
        subtitulo="Enviamos um código para o e-mail cadastrado."
      >
        <div className="flex flex-col gap-4">
          <form
            className="flex flex-col gap-3"
            onSubmit={(e) => {
              e.preventDefault();
              pedir.mutate();
            }}
          >
            <Campo
              rotulo="Seu e-mail"
              type="email"
              required
              autoComplete="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
            />
            <Botao type="submit" variante="secundario" carregando={pedir.isPending} larguraTotal>
              {pedir.isSuccess ? 'Enviar outro código' : 'Enviar código'}
            </Botao>
          </form>

          {pedir.isSuccess && (
            <Aviso tom="informacao" titulo="Se o e-mail existir, o código chegou">
              A resposta é a mesma para endereço cadastrado e não cadastrado — assim ninguém
              descobre quem tem conta testando e-mails. Confira também a caixa de spam.
            </Aviso>
          )}
          {pedir.isError && <ErroDaApi titulo="Não foi possível enviar" erro={pedir.error} />}

          {pedir.isSuccess && (
            <form
              className="flex flex-col gap-3 border-t border-borda pt-4"
              onSubmit={(e) => {
                e.preventDefault();
                confirmar.mutate();
              }}
            >
              <Campo
                rotulo="Código recebido"
                required
                maxLength={8}
                // 8 caracteres alfanuméricos: teclado numérico travaria as letras.
                ajuda="8 caracteres, como vieram no e-mail."
                className="valor-fiscal uppercase"
                value={codigo}
                onChange={(e) => setCodigo(e.target.value.toUpperCase())}
              />
              <Campo
                rotulo="Nova senha"
                type="password"
                required
                autoComplete="new-password"
                ajuda="A nova senha passa a valer imediatamente."
                value={senha}
                onChange={(e) => setSenha(e.target.value)}
              />
              <Campo
                rotulo="Repita a nova senha"
                type="password"
                required
                autoComplete="new-password"
                value={confirmacao}
                onChange={(e) => setConfirmacao(e.target.value)}
              />

              {confirmacao !== '' && !senhasBatem && (
                <p className="text-xs text-erro">As senhas não são iguais.</p>
              )}

              {confirmar.isError && (
                <ErroDaApi titulo="Não foi possível trocar a senha" erro={confirmar.error} />
              )}

              <Botao
                type="submit"
                carregando={confirmar.isPending}
                disabled={codigo.trim().length !== 8 || !senhasBatem}
                larguraTotal
              >
                Trocar senha e entrar
              </Botao>
            </form>
          )}
        </div>
      </Cartao>

      <p className="text-center text-sm text-conteudo-secundario">
        Lembrou?{' '}
        <Link to="/entrar" className="text-acento-ciano underline-offset-4 hover:underline">
          Voltar ao acesso
        </Link>
      </p>
    </main>
  );
}
